import React, { useEffect, useState } from 'react';
import { StatusFormSetting } from '../../../AssetDetails/AssetTabs';
import LoaderOverlay from '../../../common/Loader/LoaderOverlay';

interface FieldState {
    [key: string]: string;
}

type SetPageFunction = React.Dispatch<React.SetStateAction<{ [key: string]: FieldState; }>>;
type SetTabPageFunction = React.Dispatch<React.SetStateAction<string>>
type SetChangeFieldFunction = React.Dispatch<React.SetStateAction<{
    [formName: string]: Set<string>;
}>>

interface SystemFormProps {
    formState: { [key: string]: FieldState };
    setFormState: SetPageFunction;
    setTabDivHeight: SetTabPageFunction;
    SystemFormData: StatusFormSetting[] | undefined;
    changedFields: { [formName: string]: Set<string> };
    setChangedFields: SetChangeFieldFunction;
    formNames: string;
    isToggle: boolean;
    description?: string;
    isSetPointLoading?: boolean;
}

interface InitialValues {
    [formName: string]: {
        [fieldName: string]: string;
    };
}

const SystemFormTablet: React.FC<SystemFormProps> = ({ formState, setFormState, setTabDivHeight, SystemFormData, changedFields, setChangedFields, formNames, isToggle, isSetPointLoading }) => {
    const [initialValues, setInitialValues] = useState<{ [formName: string]: { [fieldName: string]: string } }>({});
    const FormName = formNames
    const showEnableOptToggle = true

    // Conditional enable/disable logic for Typical Line Pressure
    const typicalLinePressureMode = formState[FormName]?.typicalLinePressureMode;
    const isTypicalLinePressureDisabled = typicalLinePressureMode === "0";

    useEffect(() => {
        setTabDivHeight("1250px");
    }, []);


    useEffect(() => {
        // Initialize initial values when the component mounts or when form data is initialized
        const initialValues: InitialValues = {};
        // Initialize initial values for each form and field
        // Assuming formState has been initialized with initial form data
        Object.keys(formState).forEach(formName => {
            initialValues[formName] = {};
            Object.keys(formState[formName]).forEach(fieldName => {
                initialValues[formName][fieldName] = formState[formName][fieldName];
            });
        });
        setInitialValues(initialValues);
        setChangedFields({})
    }, [FormName, isToggle]);
    // Handle input change for a specific field in a specific form
    const handleInputChange = (formName: string, fieldName: string, value: string) => {
        setFormState((prevFormState) => ({
            ...prevFormState,
            [formName]: {
                ...prevFormState[formName],
                [fieldName]: value,
            },
        }));

        // setChangedFields((prevChangedFields) => ({
        //     ...prevChangedFields,
        //     [formName]: new Set<string>([...(prevChangedFields[formName] || []), fieldName]),
        // }));
    };

    // Function to identify change in field value
    const handleFieldChange = (formName: string, fieldName: string, newValue: string) => {
        const initialValue = initialValues[formName]?.[fieldName];
        if (initialValue !== newValue) {
            setChangedFields((prevChangedFields) => ({
                ...prevChangedFields,
                [formName]: new Set<string>([...(prevChangedFields[formName] || []), fieldName]),
            }));
        } else if (initialValue === newValue) {
            setChangedFields((prevChangedFields) => {
                const updatedFields = new Set<string>(prevChangedFields[formName] || []);
                updatedFields.delete(fieldName); // Remove the field name if the value reverts back to the initial value
                return {
                    ...prevChangedFields,
                    [formName]: updatedFields,
                };
            });
        }
    };

    if (isSetPointLoading) {
        return <LoaderOverlay />
    }


    return (
        <div style={{ paddingLeft: "15px" }}>
            {/* System Form */}
            <div style={{ display: 'flex', flexDirection: 'row', columnGap: '40px', rowGap: "6px", flexWrap: 'wrap' }}>
                {SystemFormData?.map(({ id, value, name, label, type, options, enable, description, unit }, index) => {

                    // Group Serial Number and SPIFFS File Status vertically in the same column
                    if (id === 9002) {
                        const spiffsField = SystemFormData.find(f => f.id === 10035);
                        return (
                            <div key="serial-spiffs-group" style={{ flexBasis: '22%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {/* Serial Number */}
                                <div>
                                    <div className="tooltip-container">
                                        <label style={{ display: 'block', marginBottom: '2px', marginLeft: "3px" }}>
                                            {label}
                                        </label>
                                        {description && <span className="tooltip-text">{description}</span>}
                                    </div>
                                    <input
                                        type="text"
                                        value={formState[FormName]?.[name] ?? value}
                                        disabled
                                        style={{
                                            visibility: enable ? "visible" : "hidden",
                                            color: 'white',
                                            padding: "6px",
                                            borderRadius: '8px',
                                            textAlign: 'right',
                                            backgroundColor: changedFields[FormName]?.has(name)
                                                ? "rgb(208 196 64)"
                                                : "rgb(5 17 35)",
                                            width: "105%",
                                            paddingRight: unit ? "3.5rem" : "6px"
                                        }}
                                        placeholder={label}
                                    />
                                </div>
                                {/* SPIFFS File Status */}
                                {spiffsField && (
                                    <div>
                                        <div className="tooltip-container">
                                            <label style={{ display: 'block', marginBottom: '2px', marginLeft: "3px" }}>
                                                {spiffsField.label}
                                            </label>
                                            {spiffsField.description && <span className="tooltip-text">{spiffsField.description}</span>}
                                        </div>
                                        <input
                                            type="text"
                                            value={formState[FormName]?.[spiffsField.name] ?? spiffsField.value}
                                            disabled
                                            style={{
                                                visibility: spiffsField.enable ? "visible" : "hidden",
                                                color:
                                                    (formState[FormName]?.[spiffsField.name] === "OK"
                                                        ? "limegreen"
                                                        : formState[FormName]?.[spiffsField.name] === "Corrupt"
                                                            ? "orangered"
                                                            : "white"),
                                                padding: "6px",
                                                borderRadius: '8px',
                                                textAlign: 'left',
                                                backgroundColor: changedFields[FormName]?.has(spiffsField.name)
                                                    ? "rgb(208 196 64)"
                                                    : "rgb(5 17 35)",
                                                width: "105%",
                                                paddingRight: spiffsField.unit ? "3.5rem" : "6px"
                                            }}
                                            placeholder={spiffsField.label}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    }

                    // Skip rendering SPIFFS File Status separately
                    if (id === 10035) return null;

                    // check pobMethod value from formState
                    const currentPobMethod = formState[FormName]?.pobMethod;
                    const autoOptEnabled = formState[FormName]?.autoOptimization?.toLocaleLowerCase() === "1";

                    if (name === "autoOptimization") {
                        return null;
                    }

                    // if Statistical → hide pobA/pobB/pobC
                    if (currentPobMethod === "0" && ["pobA", "pobB", "pobC", "bottomSensitivity"].includes(name)) {

                        if (name === "pobA") {
                            const sensitivityField = SystemFormData.find(f => f.name === "bottomSensitivity");
                            if (sensitivityField) {
                                return (
                                    <div key={sensitivityField.id} style={{ flexBasis: '20%' }}>
                                        <div className="tooltip-container">
                                            <label style={{ display: 'block', marginBottom: '2px', marginLeft: "3px" }}>
                                                {sensitivityField.label}
                                            </label>
                                            {sensitivityField.description && (
                                                <span className="tooltip-text">{sensitivityField.description}</span>
                                            )}
                                        </div>

                                        <div className="relative w-full">
                                            <div
                                                className="absolute top-0 left-0 w-full h-full pointer-events-none flex items-center justify-end pr-2"
                                                style={{
                                                    color: "var(--Primary-Indigo-500, #0094BD)",
                                                    fontSize: "18px",
                                                    padding: "6px",
                                                    borderRadius: "8px",
                                                    textAlign: "right",
                                                }}
                                            >
                                                {unit ? `| ${unit}` : ""}
                                            </div>
                                            <input
                                                type="number"
                                                value={formState[FormName] ? formState[FormName][sensitivityField.name] : sensitivityField.value}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    handleInputChange(FormName, sensitivityField.name, newValue);
                                                    handleFieldChange(FormName, sensitivityField.name, newValue);
                                                }}
                                                style={{
                                                    visibility: sensitivityField.enable ? "visible" : "hidden",
                                                    color: 'white',
                                                    padding: "6px",
                                                    borderRadius: '8px',
                                                    textAlign: 'right',
                                                    backgroundColor: changedFields[FormName]?.has(sensitivityField.name)
                                                        ? "rgb(208 196 64)"
                                                        : "rgb(5 17 35)",
                                                    width: "110%",
                                                    paddingRight: sensitivityField.unit ? "3.5rem" : "6px"
                                                }}
                                                placeholder={sensitivityField.label}
                                            />
                                        </div>
                                    </div>
                                );
                            }
                        }

                        return <div style={{ flexBasis: '20%' }}>
                            <input
                                style={{
                                    visibility: "hidden",
                                    color: 'white',
                                    padding: "6px",
                                    borderRadius: '8px',
                                    textAlign: 'right',
                                    backgroundColor: changedFields[FormName]?.has(name)
                                        ? "rgb(208 196 64)"
                                        : "rgb(5 17 35)",
                                    width: "110%",
                                    paddingRight: unit ? "3.5rem" : "6px"
                                }}
                                placeholder={label}

                            /></div>;
                    }

                    //  Case 2: pobMethod = 1 → hide bottomSensitivity, show toggle
                    if (currentPobMethod === "1" && name === "bottomSensitivity") {
                        if (showEnableOptToggle) {
                            return (
                                <div key="autoOptToggle" style={{ flexBasis: '20%' }}>
                                    <label style={{ display: 'block', marginBottom: '2px', marginLeft: "3px" }}>
                                        Enable automatic optimization
                                    </label>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={autoOptEnabled}
                                            onChange={(e) => {
                                                const newValue = e.target.checked ? "1" : "0";
                                                handleInputChange(FormName, "autoOptimization", newValue);
                                                handleFieldChange(FormName, "autoOptimization", newValue);
                                            }}
                                            style={{
                                                transform: "scale(1.5)", marginTop: "6px",
                                                backgroundColor: changedFields[FormName]?.has(name)
                                                    ? "rgb(208 196 64)"
                                                    : "rgb(5 17 35)"
                                            }}
                                        />
                                        <span
                                            className="slider"
                                            style={
                                                changedFields[FormName]?.has("autoOptimization")
                                                    ? { backgroundColor: "rgb(208 196 64)" }
                                                    : {}
                                            }
                                        />
                                    </label>

                                </div>
                            );
                        }
                        else {
                            // hide both bottomSensitivity + toggle
                            return <div><input style={{ visibility: "hidden" }}></input></div>;
                        }
                    }

                    if (currentPobMethod === "1" && ["pobA", "pobB", "pobC"].includes(name)) {
                        return (
                            <div key={id} style={{ flexBasis: '20%' }}>
                                <div className="tooltip-container">
                                    <label style={{ display: 'block', marginBottom: '2px', marginLeft: "3px" }}>
                                        {label}
                                    </label>
                                    {description && <span className="tooltip-text">{description}</span>}
                                </div>
                                <input
                                    type="number"
                                    value={formState[FormName]?.[name] ?? value}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        handleInputChange(FormName, name, newValue);
                                        handleFieldChange(FormName, name, newValue);
                                    }}
                                    disabled={autoOptEnabled}
                                    style={{
                                        visibility: enable ? "visible" : "hidden",
                                        color: 'white',
                                        padding: "6px",
                                        borderRadius: '8px',
                                        textAlign: 'right',
                                        backgroundColor: autoOptEnabled
                                            ? "#4A5463"
                                            : changedFields[FormName]?.has(name) ? "rgb(208 196 64)" : "rgb(5 17 35)",
                                        width: "110%",
                                        paddingRight: unit ? "3.5rem" : "6px"
                                    }}
                                    placeholder={label}
                                />
                            </div>
                        );
                    }

                    if (currentPobMethod === "2" && ["pobA", "pobB", "pobC", "bottomSensitivity"].includes(name)) {
                        // remapping dictionary
                        const fieldMapping: Record<string, string> = {
                            pobA: "bottomSensitivity",
                            pobB: "pobA",
                            pobC: "pobB",
                            bottomSensitivity: "pobC"
                        };

                        // use the mapped field instead of original
                        const mappedName = fieldMapping[name] || name;

                        // find the mapped field from API data
                        const mappedField = SystemFormData.find(f => f.name === mappedName);

                        const mappedLabel = mappedField?.label || label;
                        const mappedDescription = mappedField?.description || description;

                        return (
                            <div key={id} style={{ flexBasis: '20%' }}>
                                <div className="tooltip-container">
                                    <label style={{ display: 'block', marginBottom: '2px', marginLeft: "3px" }}>
                                        {mappedLabel}
                                    </label>
                                    {mappedDescription && (
                                        <span className="tooltip-text">{mappedDescription}</span>
                                    )}
                                </div>
                                <input
                                    type="number"
                                    value={formState[FormName]?.[mappedName] ?? value}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        handleInputChange(FormName, mappedName, newValue);
                                        handleFieldChange(FormName, mappedName, newValue);
                                    }}
                                    disabled={autoOptEnabled && name !== "pobA"}
                                    style={{
                                        visibility: enable ? "visible" : "hidden",
                                        color: 'white',
                                        padding: "6px",
                                        borderRadius: '8px',
                                        textAlign: 'right',
                                        backgroundColor: autoOptEnabled && name !== "pobA"
                                            ? "#4A5463"
                                            : changedFields[FormName]?.has(mappedName) ? "rgb(208 196 64)" : "rgb(5 17 35)",
                                        width: "110%",
                                        paddingRight: unit ? "3.5rem" : "6px"
                                    }}
                                    placeholder={mappedLabel}
                                />
                            </div>
                        );
                    }


                    // Case 2: pobMethod = 2 → hide bottomSensitivity, show toggle
                    if (currentPobMethod === "2" && id === 6999 && showEnableOptToggle) {
                        return (
                            <div key="autoOptToggle" style={{ flexBasis: '20%' }}>
                                <label style={{ display: 'block', marginBottom: '2px', marginLeft: "3px" }}>
                                    Enable automatic optimization
                                </label>
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={autoOptEnabled}
                                        onChange={(e) => {
                                            const newValue = e.target.checked ? "1" : "0";
                                            handleInputChange(FormName, "autoOptimization", newValue);
                                            handleFieldChange(FormName, "autoOptimization", newValue);
                                        }}
                                        style={{
                                            transform: "scale(1.5)",
                                            marginTop: "6px",
                                            backgroundColor: changedFields[FormName]?.has(name)
                                                ? "rgb(208 196 64)"
                                                : "rgb(5 17 35)"
                                        }}
                                    />
                                    <span
                                        className="slider"
                                        style={
                                            changedFields[FormName]?.has("autoOptimization")
                                                ? { backgroundColor: "rgb(208 196 64)" }
                                                : {}
                                        }
                                    />
                                </label>

                            </div>
                        );
                    }

                    // Typical Line Pressure field: disable if mode is Auto
                    if (name === "typicalLinePressure") {
                        return (
                            <div key={id} style={{ flexBasis: '20%' }}>
                                {enable && (
                                    <div className="tooltip-container">
                                        <label style={{ display: 'block', marginBottom: '2px', marginLeft: "3px" }}>
                                            {label}
                                        </label>
                                        {description && <span className="tooltip-text">{description}</span>}
                                    </div>
                                )}
                                <div className="relative w-full">
                                    <div
                                        className="absolute top-0 left-0 w-full h-full pointer-events-none flex items-center justify-end pr-2"
                                        style={{
                                            color: "var(--Primary-Indigo-500, #0094BD)",
                                            fontSize: "18px",
                                            padding: "6px",
                                            borderRadius: "8px",
                                            textAlign: "right",
                                        }}
                                    >
                                        {unit ? `| ${unit}` : ""}
                                    </div>
                                    <input
                                        key={id}
                                        type="number"
                                        value={formState[FormName] ? formState[FormName][name] : value}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            handleInputChange(FormName, name, newValue);
                                            handleFieldChange(FormName, name, newValue);
                                        }}
                                        style={{
                                            visibility: enable ? "visible" : "hidden",
                                            color: 'white',
                                            padding: "6px",
                                            borderRadius: '8px',
                                            textAlign: 'right',
                                            backgroundColor: isTypicalLinePressureDisabled ? "#4A5463" : changedFields[FormName]?.has(name) ? "rgb(208 196 64)" : "rgb(5 17 35)",
                                            width: "110%",
                                            paddingRight: unit ? "3.5rem" : "6px"
                                        }}
                                        placeholder={label}
                                        disabled={isTypicalLinePressureDisabled}
                                    />
                                </div>
                            </div>
                        );
                    }

                    //  Normal rendering for all other fields
                    return (
                        <div key={id} style={{ flexBasis: '20%' }}>
                            {enable && (
                                <div className="tooltip-container">
                                    <label style={{ display: 'block', marginBottom: '2px', marginLeft: "3px" }}>
                                        {label}
                                    </label>
                                    {description && <span className="tooltip-text">{description}</span>}
                                </div>
                            )}
                            {type !== "dropdown" ? (
                                <div className="relative w-full">
                                    <div
                                        className="absolute top-0 left-0 w-full h-full pointer-events-none flex items-center justify-end pr-2"
                                        style={{
                                            color: "var(--Primary-Indigo-500, #0094BD)",
                                            fontSize: "18px",
                                            padding: "6px",
                                            borderRadius: "8px",
                                            textAlign: "right",
                                        }}
                                    >
                                        {unit ? `| ${unit}` : ""}
                                    </div>
                                    <input
                                        key={id}
                                        type={id === 9002 ? "text" : "number"}
                                        value={formState[FormName] ? formState[FormName][name] : value}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            handleInputChange(FormName, name, newValue);
                                            handleFieldChange(FormName, name, newValue);
                                        }}
                                        style={{
                                            visibility: enable ? "visible" : "hidden",
                                            color: 'white',
                                            padding: "6px",
                                            borderRadius: '8px',
                                            textAlign: 'right',
                                            backgroundColor: changedFields[FormName]?.has(name)
                                                ? "rgb(208 196 64)"
                                                : "rgb(5 17 35)",
                                            width: "110%",
                                            paddingRight: unit ? "3.5rem" : "6px"
                                        }}
                                        placeholder={`${label} ${id}`}
                                        disabled={id === 9002 ? true : false}
                                    />
                                </div>
                            ) : (
                                <select
                                    value={formState[FormName] ? formState[FormName][name] : value}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        handleInputChange(FormName, name, newValue);
                                        handleFieldChange(FormName, name, newValue);
                                    }}
                                    style={{
                                        visibility: enable ? "visible" : "hidden",
                                        color: 'white',
                                        padding: "6px",
                                        borderRadius: '8px',
                                        textAlign: 'left',
                                        backgroundColor: changedFields[FormName]?.has(name)
                                            ? "rgb(208 196 64)"
                                            : "rgb(5 17 35)",
                                        width: "110%",
                                    }}
                                >
                                    {options?.map((option: any) => (
                                        <option key={option.value} value={option.value}>
                                            {option.text}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    );
                })}

            </div>
        </div>
    );
};

export default SystemFormTablet;
